import { useCallback, useEffect, useRef, useState } from 'react';
import { Toast } from 'antd-mobile';
import { io, Socket } from 'socket.io-client';
import i18n from '../../../i18n';
import { getApiOrigin } from '../../../config/api';
import { useAuthStore } from '../../../stores/auth.store';
import {
  ApiEnvelope,
  customerServiceApi,
  CustomerServiceConversation,
  CustomerServiceMessage,
  PaginatedResponse,
} from '../../../api/customer-service';
import { CustomerServiceViewState } from './types';

type ApiErrorLike = { response?: { data?: { error?: { message?: string } } } };

function readErrorMessage(error: ApiErrorLike, fallback: string): string {
  return error.response?.data?.error?.message || fallback;
}

export function useCustomerServiceModel() {
  const accessToken = useAuthStore((state) => state.accessToken);
  const socketRef = useRef<Socket | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const [state, setState] = useState<CustomerServiceViewState>({
    loading: true,
    conversation: null,
    messages: [],
    messageDraft: '',
    sending: false,
  });

  const loadMessages = useCallback(async (conversationId: string) => {
    const response = await customerServiceApi.listMessages(conversationId, { page: 1, pageSize: 100 }) as { data: PaginatedResponse<CustomerServiceMessage> };
    await customerServiceApi.markRead(conversationId);
    setState((previous) => ({ ...previous, messages: response.data.data }));
  }, []);

  const initializeConversation = useCallback(async () => {
    setState((previous) => ({ ...previous, loading: true }));
    try {
      const conversationResponse = await customerServiceApi.getMyConversation() as { data: ApiEnvelope<CustomerServiceConversation> };
      const conversation = conversationResponse.data.data;
      conversationIdRef.current = conversation.id;
      socketRef.current?.emit('customer_service:join', { conversationId: conversation.id });
      setState((previous) => ({ ...previous, conversation, loading: false }));
      await loadMessages(conversation.id);
    } catch (error) {
      Toast.show({ icon: 'fail', content: readErrorMessage(error as ApiErrorLike, i18n.t('customerService:loadFailed')) });
      setState((previous) => ({ ...previous, loading: false }));
    }
  }, [loadMessages]);

  const setMessageDraft = useCallback((messageDraft: string) => {
    setState((previous) => ({ ...previous, messageDraft }));
  }, []);

  const sendMessage = useCallback(async () => {
    const conversationId = conversationIdRef.current;
    const content = state.messageDraft.trim();
    if (!conversationId || !content || state.sending) {
      return;
    }

    setState((previous) => ({ ...previous, sending: true }));
    try {
      if (socketRef.current?.connected) {
        socketRef.current.emit('customer_service:send', { conversationId, content }, (ok: boolean) => {
          if (!ok) {
            Toast.show({ icon: 'fail', content: i18n.t('customerService:sendFailedToast') });
          }
        });
      } else {
        const response = await customerServiceApi.sendMessage(conversationId, content) as { data: ApiEnvelope<CustomerServiceMessage> };
        setState((previous) => ({ ...previous, messages: [...previous.messages, response.data.data] }));
      }
      setState((previous) => ({ ...previous, messageDraft: '' }));
    } catch (error) {
      Toast.show({ icon: 'fail', content: readErrorMessage(error as ApiErrorLike, i18n.t('customerService:sendFailedFallback')) });
    } finally {
      setState((previous) => ({ ...previous, sending: false }));
    }
  }, [state.messageDraft, state.sending]);

  useEffect(() => {
    void initializeConversation();
  }, [initializeConversation]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    const socket = io(getApiOrigin() || window.location.origin, {
      auth: { token: accessToken },
      path: '/socket.io',
      transports: ['websocket', 'polling'],
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      const conversationId = conversationIdRef.current;
      if (conversationId) {
        socket.emit('customer_service:join', { conversationId });
      }
    });

    socket.on('customer_service:message', (incomingMessage: CustomerServiceMessage) => {
      if (incomingMessage.conversation_id !== conversationIdRef.current) {
        return;
      }

      setState((previous) => {
        if (previous.messages.some((item) => item.id === incomingMessage.id)) {
          return previous;
        }
        return { ...previous, messages: [...previous.messages, incomingMessage] };
      });
      socket.emit('customer_service:read', { conversationId: incomingMessage.conversation_id });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [accessToken]);

  return {
    state,
    setMessageDraft,
    sendMessage,
    refresh: initializeConversation,
  };
}
